/**
 * File containing common date utils to be used across the application
 * All methods here will be concerned with handling and manipulating dates for display on the UI
 * TODO: Add tests for these methods
 */

import moment, { type Moment } from "moment-timezone";


export function formatTime(date?: Date | null) {
    if (date) {
        const originalDate = new Date(date);
        const AMPM = originalDate.getHours() >= 12 ? 'PM' : 'AM';
        const hours = originalDate.getHours() % 12 === 0 ? 12 : originalDate.getHours() % 12;
        const minutes = originalDate.getMinutes();
        const formattedTime = `${hours}:` + (minutes < 10 ? `0${minutes}` : minutes.toString()) + ` ${AMPM}`;
        return formattedTime;
    }
}

export function formatJsonTime(time) {
    const pieces = time.split(':');
    return `${pieces[0]}:${pieces[1]}`;
}

/**
 * 
 * @param date the date to be rounded
 * @param minutes the nearest higher minutes value for the date to be rounded to
 */
export const roundTimeToNearest = (date: Date | null = new Date(), minutes: number) => {
    const verifiedDate = date ? date : new Date() // check "null" value case
    const desiredMinutesInMiliSeconds = 1000 * 60 * minutes;
    const newTimeValue = (Math.floor(verifiedDate.getTime() / desiredMinutesInMiliSeconds) + 1) * desiredMinutesInMiliSeconds
    return new Date(newTimeValue);  
}

export const areDatesEqual = (date1: Date | null, date2: Date | null) => {
    if (date1 === date2) {
        return true
    }
    if (!date1 || !date2) {
        return false
    }
    return date1.getTime() === date2.getTime()
}

export const isDateAfter = (dateToBeCompared: Date | null, date2: Date | null) => {
    if (dateToBeCompared === date2) {
        return false
    }
    if (!dateToBeCompared || !date2) {
        return false
    }
    return dateToBeCompared.getTime() > date2.getTime()
}

export const isDateBefore = (dateToBeCompared: Date | null, date2: Date | null) => {
    return !isDateAfter(dateToBeCompared, date2)
}


export const toLocaleTimeString = (date: Date | undefined | null, locale: string, showSeconds?: boolean) => {
    return date?.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: 'numeric',
        second: Boolean(showSeconds) ? 'numeric' : undefined
    });
}


export const isValid15MinuteIncrement = (date: Moment) => {
    const currentDateMinutes = date?.format('mm')
    const integerMinutes = parseInt(currentDateMinutes || '')
    return isNaN(integerMinutes) ? false : (integerMinutes % 15) === 0
}

export const isPast = (date: Date) => date.getTime() < new Date().getTime();

export const isFuture = (date: Date) => date.getTime() > new Date().getTime();

export const CA_FR_TIMEZONE_MAPPING: Record<string, string> = {
    "PST": "HNP", "PDT": "HAP",
    "MST": "HNR", "MDT": "HAR",
    "CST": "HNC", "CDT": "HAC",
    "EST": "HNE", "EDT": "HAE",
    "AST": "HNA", "ADT": "HAA",
    "NST": "HNT", "NDT": "HAT",
    "PT": "HP",
} as const;

/**
 * Converts a timezone in IANA format to its short form, e.g. "America/Toronto" => "EST" or "EDT" depending on the time of the year
 * If the short form is in unofficial offset format (e.g. "+0845"), it will be converted to "UTC+08:45" for better readability
 * 
 * @param timezone a timezone string in IANA format, e.g. "America/Toronto"
 * @returns the short form of the timezone
 */
export const toShortTimezone: (timezone: string, futureDate?: Date) => string = (timezone, futureDate) => {
    if(!timezone) {
      return "";
    }
    
    const isDst = moment(futureDate).tz(timezone).isDST();

    const nonStandardTimezonesMap: Record<string, string> = {
        "Australia/Eucla": "ACWST",
        "Pacific/Chatham": isDst ? "CHADT" : "CHAST",
        "Pacific/Norfolk": isDst ? "NFDT" : "NFT",
        "Australia/Lord_Howe": isDst ? "LHDT" : "LHST",
    };
    const timeZoneAbbr = nonStandardTimezonesMap[timezone] ?? moment(futureDate).tz(timezone).zoneAbbr() ?? timezone;
    // Handle edge case for unofficial timezone abbreviations in offset format (e.g. "+0845" or "-0530")
    if (/^(\+|\-)/.test(timeZoneAbbr)) {
        return timeZoneAbbr.length > 3 ? `UTC${timeZoneAbbr.slice(0, 3)}:${timeZoneAbbr.slice(3)}` : `UTC${timeZoneAbbr}`;
    }

    if (moment.locale()?.startsWith('fr')) {
        return CA_FR_TIMEZONE_MAPPING[timeZoneAbbr] ?? timeZoneAbbr;
    }

    return timeZoneAbbr;
};
